import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Briefcase, Heart, MapPin, Clock, Users,
  Zap, Coffee, BookOpen, Shield, Globe, Sparkles,
  Code2, PenTool, BarChart3, Headphones, ChevronRight, X, Upload, Loader2, TrendingUp
} from 'lucide-react';
import landingService from '../../../services/landingService';
import CompanyLayout from './CompanyLayout';
import './CareersPage.css';

const ICON_MAP = { Globe, Heart, BookOpen, Coffee, Shield, Zap, Code2, PenTool, BarChart3, Users, Headphones, Briefcase, TrendingUp };

const CareersPage = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [activeFilter, setActiveFilter] = useState('All');
  const [perks, setPerks] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [departments, setDepartments] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [applyJob, setApplyJob] = useState(null);
  const [applyForm, setApplyForm] = useState({ name: '', email: '', phone: '', linkedIn: '', coverLetter: '' });
  const [resumeFile, setResumeFile] = useState(null);
  const [applyStatus, setApplyStatus] = useState({ message: '', type: '' });
  const [applySubmitting, setApplySubmitting] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  // Fetch perks + openings from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [perksRes, openingsRes] = await Promise.all([
        landingService.getCareerPerks(),
        landingService.getCareerOpenings(),
      ]);
      if (perksRes.success) setPerks(perksRes.perks || []);
      if (openingsRes.success) {
        setOpenings(openingsRes.openings || []);
        const depts = openingsRes.departments || [];
        setDepartments(['All', ...depts]);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  const filteredOpenings =
    activeFilter === 'All'
      ? openings
      : openings.filter((o) => o.department === activeFilter);

  const resolveIcon = (iconName) => ICON_MAP[iconName] || Briefcase;

  const cultureHighlights = [
    'Weekly all-hands demos — everyone ships, everyone shares',
    'Quarterly virtual off-sites with team challenges',
    'Open-source Fridays — contribute to the tools you love',
    'Book club, research jams, and side-project showcases',
  ];

  const openApplyModal = (job) => {
    setApplyJob(job);
    setApplyForm({ name: '', email: '', phone: '', linkedIn: '', coverLetter: '' });
    setResumeFile(null);
    setApplyStatus({ message: '', type: '' });
  };

  const closeApplyModal = () => {
    setApplyJob(null);
    setApplyStatus({ message: '', type: '' });
  };

  const handleApplyChange = (e) => {
    setApplyForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!applyJob) return;
    setApplySubmitting(true);
    setApplyStatus({ message: '', type: '' });

    const formData = new FormData();
    formData.append('name', applyForm.name);
    formData.append('email', applyForm.email);
    if (applyForm.phone) formData.append('phone', applyForm.phone);
    if (applyForm.linkedIn) formData.append('linkedIn', applyForm.linkedIn);
    if (applyForm.coverLetter) formData.append('coverLetter', applyForm.coverLetter);
    if (resumeFile) formData.append('resume', resumeFile);

    const result = await landingService.submitApplication(applyJob._id, formData);
    setApplyStatus({
      message: result.message || (result.success ? 'Application submitted! Check your email for confirmation.' : 'Something went wrong.'),
      type: result.success ? 'success' : 'error',
    });
    setApplySubmitting(false);
    if (result.success) {
      setTimeout(() => closeApplyModal(), 3000);
    }
  };

  return (
    <CompanyLayout>
      {/* Hero */}
      <section className="rsc-hero careers-hero">
        <div className="rsc-hero-inner">
          <span className="rsc-hero-badge">
            <Briefcase size={14} /> We're Hiring
          </span>
          <h1 className="rsc-hero-title">
            Shape the Future of<br />
            <span className="rsc-hero-accent">Academic Collaboration</span>
          </h1>
          <p className="rsc-hero-subtitle">
            Join a passionate, remote-first team building tools that empower
            researchers, educators, and students around the globe.
          </p>
          <a href="#openings" className="rsc-btn rsc-btn-primary">
            View Open Roles <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Perks */}
      <section
        id="perks"
        ref={(el) => (sectionRefs.current['perks'] = el)}
        className={`rsc-section ${isVisible('perks') ? 'careers-visible' : ''}`}
      >
        <div className="rsc-section-inner">
          <span className="rsc-section-label">Why ResearchPro</span>
          <h2 className="rsc-section-title">Perks & Benefits</h2>
          <p className="rsc-section-subtitle">
            We take care of our team so they can focus on doing their best work.
          </p>
          <div className="rsc-grid rsc-grid-3">
            {perks.map((perk) => {
              const Icon = resolveIcon(perk.icon);
              return (
                <div key={perk._id || perk.title} className="rsc-card careers-perk-card">
                  <div className="careers-perk-icon">
                    <Icon size={22} />
                  </div>
                  <h3 className="rsc-card-title">{perk.title}</h3>
                  <p className="rsc-card-text">{perk.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section
        id="openings"
        ref={(el) => (sectionRefs.current['openings'] = el)}
        className={`rsc-section rsc-section-alt ${isVisible('openings') ? 'careers-visible' : ''}`}
      >
        <div className="rsc-section-inner">
          <span className="rsc-section-label">Open Positions</span>
          <h2 className="rsc-section-title">Find Your Role</h2>
          <p className="rsc-section-subtitle">
            {openings.length} open positions across {departments.length - 1} departments.
          </p>

          {/* Filters */}
          <div className="careers-filters">
            {departments.map((dept) => (
              <button
                key={dept}
                className={`careers-filter-btn ${activeFilter === dept ? 'careers-filter-active' : ''}`}
                onClick={() => setActiveFilter(dept)}
              >
                {dept}
              </button>
            ))}
          </div>

          {/* Listings */}
          <div className="careers-listings">
            {loading ? (
              <p className="careers-no-results">Loading positions...</p>
            ) : filteredOpenings.map((job) => {
              const Icon = resolveIcon(job.icon);
              return (
                <div key={job._id || job.title} className="careers-job-card">
                  <div className="careers-job-left">
                    <div className="careers-job-icon">
                      <Icon size={18} />
                    </div>
                    <div>
                      <h3 className="careers-job-title">{job.title}</h3>
                      <p className="careers-job-desc">{job.description}</p>
                      <div className="careers-job-meta">
                        <span><MapPin size={13} /> {job.location}</span>
                        <span><Clock size={13} /> {job.type}</span>
                        <span className="careers-dept-tag">{job.department}</span>
                      </div>
                    </div>
                  </div>
                  {job.applyUrl ? (
                    <a href={job.applyUrl} target="_blank" rel="noopener noreferrer" className="careers-apply-btn">
                      Apply <ChevronRight size={15} />
                    </a>
                  ) : (
                    <button className="careers-apply-btn" onClick={() => openApplyModal(job)}>
                      Apply <ChevronRight size={15} />
                    </button>
                  )}
                </div>
              );
            })}
            {filteredOpenings.length === 0 && (
              <p className="careers-no-results">
                No openings in this department right now — check back soon!
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Culture */}
      <section
        id="culture"
        ref={(el) => (sectionRefs.current['culture'] = el)}
        className={`rsc-section ${isVisible('culture') ? 'careers-visible' : ''}`}
      >
        <div className="rsc-section-inner careers-culture-inner">
          <div className="careers-culture-text">
            <span className="rsc-section-label">Our Culture</span>
            <h2 className="rsc-section-title" style={{ textAlign: 'left' }}>
              Built on Openness,<br />Driven by Curiosity
            </h2>
            <p className="careers-culture-desc">
              We're researchers, builders, and lifelong learners united by the belief
              that collaboration makes everyone better. No corporate fluff — just
              smart, kind people doing meaningful work.
            </p>
          </div>
          <div className="careers-culture-list">
            {cultureHighlights.map((item, i) => (
              <div key={i} className="careers-culture-item">
                <Sparkles size={16} className="careers-culture-icon" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        id="careers-cta"
        ref={(el) => (sectionRefs.current['careers-cta'] = el)}
        className={`rsc-cta ${isVisible('careers-cta') ? 'rsc-cta--visible' : ''}`}
      >
        <div className="rsc-cta-inner">
          <span className="rsc-cta-badge">
            <Zap size={14} />
            We're Hiring
          </span>
          <h2 className="rsc-cta-title">Don't See Your Role?</h2>
          <p className="rsc-cta-text">
            We're always on the lookout for exceptional people. Send us your resume
            and tell us how you'd like to contribute.
          </p>
          <div className="rsc-cta-actions">
            <Link to="/contact" className="rsc-btn rsc-btn--primary">
              Get in Touch
              <ArrowRight size={16} />
            </Link>
            <Link to="/about" className="rsc-btn rsc-btn--secondary">
              Learn About Us
            </Link>
          </div>
        </div>
      </section>
      {/* Apply Modal */}
      {applyJob && (
        <div className="careers-modal-overlay" onClick={closeApplyModal}>
          <div className="careers-modal" onClick={(e) => e.stopPropagation()}>
            <button className="careers-modal-close" onClick={closeApplyModal}>
              <X size={20} />
            </button>

            <div className="careers-modal-header">
              <h3 className="careers-modal-title">Apply for {applyJob.title}</h3>
              <p className="careers-modal-subtitle">{applyJob.department} · {applyJob.location} · {applyJob.type}</p>
            </div>

            {applyStatus.type === 'success' ? (
              <div className="careers-modal-success">
                <div className="careers-modal-success-icon">✓</div>
                <p>{applyStatus.message}</p>
              </div>
            ) : (
              <form className="careers-modal-form" onSubmit={handleApplySubmit}>
                <div className="careers-form-row">
                  <div className="careers-form-group">
                    <label>Name *</label>
                    <input type="text" name="name" value={applyForm.name} onChange={handleApplyChange} required placeholder="Jane Doe" />
                  </div>
                  <div className="careers-form-group">
                    <label>Email *</label>
                    <input type="email" name="email" value={applyForm.email} onChange={handleApplyChange} required placeholder="jane@university.edu" />
                  </div>
                </div>

                <div className="careers-form-row">
                  <div className="careers-form-group">
                    <label>Phone</label>
                    <input type="tel" name="phone" value={applyForm.phone} onChange={handleApplyChange} placeholder="+1 (555) 000-0000" />
                  </div>
                  <div className="careers-form-group">
                    <label>LinkedIn</label>
                    <input type="url" name="linkedIn" value={applyForm.linkedIn} onChange={handleApplyChange} placeholder="https://linkedin.com/in/..." />
                  </div>
                </div>

                <div className="careers-form-group">
                  <label>Cover Letter</label>
                  <textarea name="coverLetter" value={applyForm.coverLetter} onChange={handleApplyChange} rows={4} placeholder="Tell us why you're interested in this role..." />
                </div>

                <div className="careers-form-group">
                  <label>Resume (PDF, max 5MB)</label>
                  <label className="careers-file-input">
                    <Upload size={16} />
                    <span>{resumeFile ? resumeFile.name : 'Choose file...'}</span>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setResumeFile(e.target.files[0] || null)}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                {applyStatus.type === 'error' && (
                  <p className="careers-form-error">{applyStatus.message}</p>
                )}

                <button type="submit" className="careers-form-submit" disabled={applySubmitting}>
                  {applySubmitting ? (
                    <><Loader2 size={16} className="careers-spinner" /> Submitting...</>
                  ) : (
                    <>Submit Application <ArrowRight size={16} /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

    </CompanyLayout>
  );
};

export default CareersPage;
